import { partyRows } from '@khata/core';
import { DateInput, Field, MoneyInput, TextInput } from '../components/index.js';
import { Picker } from '../components/Picker.js';
import { AccountPicker } from '../components/AccountPicker.js';
import { fromApi, isErr, money, useFields, useSave } from '../lib/form.js';
import { taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { master, state, submit, today } from '../lib/khata.js';
import { FormErrorLine, SaveButton, Sheet } from './Sheet.js';

export function PaymentSheet(props: {
  direction: 'in' | 'out';
  partyId?: string;
  amount?: number;
}) {
  const m = master.value;
  const s = state.value;
  const isIn = props.direction === 'in';
  const rows = partyRows(s, m, isIn ? 'customer' : 'vendor').filter(
    (r) => r.party.active || r.party.id === props.partyId,
  );
  const accounts = [...m.accounts.values()].filter((a) => a.active);
  const [f, set] = useFields({
    partyId: props.partyId ?? '',
    amount: props.amount ? String(props.amount / 100) : '',
    accountId: accounts[0]?.id ?? '',
    date: today.value,
    note: '',
  });
  const [error, busy, save] = useSave();

  const onSave = () =>
    save(async () => {
      if (!f.partyId) return { message: t('payment_choose'), field: 'partyId' };
      const amount = money(f.amount, 'amount', { min: 1 });
      if (isErr(amount)) return amount;
      const r = await submit('payment', {
        date: f.date,
        partyId: f.partyId,
        direction: props.direction,
        amount,
        accountId: f.accountId,
        note: f.note,
      });
      if (!r.ok) return fromApi(r.error);
      return t(isIn ? 'payment_received' : 'payment_paid', { amount: taka(amount) });
    });

  return (
    <Sheet
      title={isIn ? t('sheet_receivePayment') : t('sheet_payVendor')}
      footer={<SaveButton busy={busy} onClick={onSave} />}
    >
      <Field label={isIn ? t('customer') : t('vendor')}>
        <Picker
          value={f.partyId}
          onChange={(v) => set('partyId', v)}
          placeholder={isIn ? t('picker_customer') : t('picker_vendor')}
          invalid={error?.field === 'partyId'}
          autoFocus={!props.partyId}
          testId="party-picker"
          options={rows.map((r) => ({
            id: r.party.id,
            label: r.party.name,
            sub: r.party.phone || r.party.note,
            right:
              r.status === 'due'
                ? t('payment_dueSuffix', { amount: taka(r.balance) })
                : r.status === 'advance'
                  ? t('payment_advanceSuffix', { amount: taka(-r.balance as never) })
                  : '',
          }))}
        />
      </Field>
      <div class="grid-2">
        <Field label={t('amountTaka')}>
          <MoneyInput
            value={f.amount}
            onInput={(v) => set('amount', v)}
            invalid={error?.field === 'amount'}
            autoFocus={!!props.partyId}
          />
        </Field>
        <Field label={t('payment_via')}>
          <AccountPicker
            value={f.accountId}
            onChange={(v) => set('accountId', v)}
            accounts={accounts}
            testId="account-picker"
          />
        </Field>
      </div>
      <div class="grid-2">
        <Field label={t('date')}>
          <DateInput
            value={f.date}
            onInput={(v) => set('date', v)}
            invalid={error?.field === 'date'}
          />
        </Field>
        <Field label={t('note')}>
          <TextInput
            value={f.note}
            onInput={(v) => set('note', v)}
            placeholder={t('payment_notePlaceholder')}
          />
        </Field>
      </div>
      <FormErrorLine error={error} />
    </Sheet>
  );
}
